import { useState, useEffect, useMemo } from 'react';
import {
  Truck, Search, CheckSquare, Square, AlertCircle,
  ChevronLeft, Package, User,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { getAlistamientosByLavanderia, marcarLoteComoEntregado } from '../services/alistamientos.services';
import { fetchUsers } from '@/pages/parametrizacion/usuarios/Services/usuarios.services';

const TIPOS_ENTREGA = [
  { value: 'ordinaria', label: 'Ordinaria' },
  { value: 'extra', label: 'Extra' },
  { value: 'dominical', label: 'Dominical' },
];

const PonerEnRutaModal = ({ isOpen, onClose, idLavanderia, onSuccess }) => {
  const { toast } = useToast();

  const [step, setStep] = useState('cliente');
  const [alistamientos, setAlistamientos] = useState([]);
  const [isLoadingAlistamientos, setIsLoadingAlistamientos] = useState(false);
  const [clienteSearch, setClienteSearch] = useState('');
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [conductores, setConductores] = useState([]);
  const [isLoadingConductores, setIsLoadingConductores] = useState(false);
  const [selectedConductorId, setSelectedConductorId] = useState('');
  const [tipoEntrega, setTipoEntrega] = useState('ordinaria');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStep('cliente');
      setSelectedCliente(null);
      setSelectedIds([]);
      setSelectedConductorId('');
      setTipoEntrega('ordinaria');
      setClienteSearch('');
      loadAlistamientos();
      loadConductores();
    }
  }, [isOpen]);

  const loadAlistamientos = async () => {
    setIsLoadingAlistamientos(true);
    try {
      const response = await getAlistamientosByLavanderia(idLavanderia, {
        query: { estado: 'Alistado' },
        limit: 1000,
        page: 1,
      });
      setAlistamientos(response.data || []);
    } catch {
      setAlistamientos([]);
    } finally {
      setIsLoadingAlistamientos(false);
    }
  };

  const loadConductores = async () => {
    setIsLoadingConductores(true);
    try {
      const response = await fetchUsers({
        limit: 100,
        offset: 1,
        query: { rol: 'conductor', estado: 'Activo' },
      });
      setConductores(response.data || []);
    } catch {
      setConductores([]);
    } finally {
      setIsLoadingConductores(false);
    }
  };

  const clientes = useMemo(() => {
    const map = {};
    alistamientos.forEach((a) => {
      const id = a.cliente?.id || a.cliente?._id;
      if (!id) return;
      if (!map[id]) map[id] = { id, nombre: a.cliente?.nombre || '-', count: 0 };
      map[id].count++;
    });
    return Object.values(map).sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [alistamientos]);

  const filteredClientes = useMemo(() => {
    if (!clienteSearch) return clientes;
    const term = clienteSearch.toLowerCase();
    return clientes.filter((c) => c.nombre.toLowerCase().includes(term));
  }, [clientes, clienteSearch]);

  const alistamientosDeCliente = useMemo(() => {
    if (!selectedCliente) return [];
    return alistamientos.filter(
      (a) => (a.cliente?.id || a.cliente?._id) === selectedCliente.id,
    );
  }, [alistamientos, selectedCliente]);

  const toggleAlistamiento = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const toggleAll = () =>
    setSelectedIds(
      selectedIds.length === alistamientosDeCliente.length
        ? []
        : alistamientosDeCliente.map((a) => a._id),
    );

  const handleSelectCliente = (cliente) => {
    setSelectedCliente(cliente);
    setSelectedIds(
      alistamientos
        .filter((a) => (a.cliente?.id || a.cliente?._id) === cliente.id)
        .map((a) => a._id),
    );
    setStep('detalles');
  };

  const selectedConductor = conductores.find((c) => c._id === selectedConductorId) || null;

  const totalPrendas = alistamientosDeCliente
    .filter((a) => selectedIds.includes(a._id))
    .reduce((sum, a) => sum + (a.totalItems || 0), 0);

  const handleConfirm = async () => {
    if (!selectedConductor || selectedIds.length === 0) return;
    setIsProcessing(true);
    try {
      await marcarLoteComoEntregado(
        selectedIds,
        { id: selectedConductor._id, nombre: selectedConductor.nombre },
        tipoEntrega,
        '',
      );
      toast({
        title: 'Éxito',
        description: `${selectedIds.length} alistamiento(s) puestos en ruta con ${selectedConductor.nombre}`,
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo poner en ruta',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[70vw] max-w-[70vw]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-blue-600" />
            Poner en Ruta
          </DialogTitle>
          <DialogDescription>
            {step === 'cliente'
              ? 'Selecciona el cliente cuyos alistamientos deseas despachar'
              : `Cliente: ${selectedCliente?.nombre}`}
          </DialogDescription>
        </DialogHeader>

        {/* ── STEP 1: clientes ── */}
        {step === 'cliente' && (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar cliente..."
                value={clienteSearch}
                onChange={(e) => setClienteSearch(e.target.value)}
                className="pl-9"
                autoFocus
              />
            </div>

            {isLoadingAlistamientos ? (
              <div className="flex justify-center py-10">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : filteredClientes.length === 0 ? (
              <div className="flex flex-col items-center py-10 text-gray-400 gap-2">
                <Package className="h-10 w-10" />
                <p className="text-sm">No hay alistamientos en estado "Alistado"</p>
              </div>
            ) : (
              <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                {filteredClientes.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => handleSelectCliente(c)}
                    className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-800">{c.nombre}</span>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {c.count} alistamiento{c.count !== 1 ? 's' : ''}
                    </Badge>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── STEP 2: alistamientos + conductor + tipo ── */}
        {step === 'detalles' && (
          <div className="space-y-4">
            {/* Alistamientos */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">Alistamientos</Label>
                <button
                  onClick={toggleAll}
                  className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                >
                  {selectedIds.length === alistamientosDeCliente.length
                    ? <><CheckSquare className="h-3.5 w-3.5" /> Deseleccionar todos</>
                    : <><Square className="h-3.5 w-3.5" /> Seleccionar todos</>}
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-48 overflow-y-auto pr-1">
                {alistamientosDeCliente.map((a) => {
                  const checked = selectedIds.includes(a._id);
                  return (
                    <button
                      key={a._id}
                      onClick={() => toggleAlistamiento(a._id)}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border transition-colors text-left ${
                        checked
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {checked
                        ? <CheckSquare className="h-4 w-4 text-blue-600 shrink-0" />
                        : <Square className="h-4 w-4 text-gray-300 shrink-0" />}
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-semibold text-purple-700 truncate">
                            {a.consecutivo}
                          </span>
                          {a.zona && (
                            <Badge variant="outline" className="text-xs py-0 px-1 shrink-0">
                              {a.zona}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">
                          {a.totalItems} prendas · {a.bolsas?.length || 0} bolsa(s)
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>

              {selectedIds.length > 0 && (
                <p className="text-xs text-blue-600 mt-1.5">
                  {selectedIds.length} seleccionado(s) · {totalPrendas} prendas
                </p>
              )}
            </div>

            {/* Tipo de entrega + Conductor en fila */}
            <div className="grid grid-cols-1 gap-4">
              <div className=" space-y-2">
                <Label className="text-sm font-semibold">Tipo de entrega</Label>
                <div className="grid grid-cols-3 gap-1.5">
                  {TIPOS_ENTREGA.map((t) => (
                    <button
                      key={t.value}
                      onClick={() => setTipoEntrega(t.value)}
                      className={`py-2 px-3 rounded-lg border text-sm font-medium transition-colors text-left flex items-center gap-2 ${
                        tipoEntrega === t.value
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <span className={`h-2 w-2 rounded-full shrink-0 ${
                        tipoEntrega === t.value ? 'bg-blue-500' : 'bg-gray-300'
                      }`} />
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold flex items-center gap-1">
                  Conductor <span className="text-red-500">*</span>
                </Label>
                {isLoadingConductores ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                  </div>
                ) : conductores.length === 0 ? (
                  <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <AlertCircle className="h-4 w-4 text-yellow-600 shrink-0 mt-0.5" />
                    <span className="text-xs text-yellow-700">
                      No hay conductores disponibles.
                    </span>
                  </div>
                ) : (
                  <Select value={selectedConductorId} onValueChange={setSelectedConductorId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona..." />
                    </SelectTrigger>
                    <SelectContent>
                      {conductores.map((c) => (
                        <SelectItem key={c._id} value={c._id}>
                          {c.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            {/* Acciones */}
            <div className="flex gap-3 pt-1">
              <Button
                variant="outline"
                onClick={() => setStep('cliente')}
                disabled={isProcessing}
                className="gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Atrás
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={isProcessing || selectedIds.length === 0 || !selectedConductorId || conductores.length === 0}
                className="flex-1 gap-2 bg-blue-600 hover:bg-blue-700"
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <Truck className="h-4 w-4" />
                    Poner en Ruta ({selectedIds.length})
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PonerEnRutaModal;
